import React, { useRef, useEffect } from 'react';
import { Box, Typography, Chip, useTheme } from '@mui/material';
import { LogEntry } from '@/services/monitoringService';

interface LogViewerProps {
  logs: LogEntry[];
  autoScroll?: boolean;
  maxHeight?: number | string;
}

const lightLevelColors: Record<string, { bg: string; text: string }> = {
  error: { bg: '#ffebee', text: '#c62828' },
  warn: { bg: '#fff3e0', text: '#ef6c00' },
  info: { bg: '#e3f2fd', text: '#1565c0' },
  http: { bg: '#f3e5f5', text: '#7b1fa2' },
  verbose: { bg: '#e8f5e9', text: '#2e7d32' },
  debug: { bg: '#e0e0e0', text: '#424242' },
  silly: { bg: '#eeeeee', text: '#757575' },
};

const darkLevelColors: Record<string, { bg: string; text: string }> = {
  error: { bg: '#4e1a1a', text: '#ef9a9a' },
  warn: { bg: '#4e3319', text: '#ffcc80' },
  info: { bg: '#1a2d4e', text: '#90caf9' },
  http: { bg: '#3b1a4e', text: '#ce93d8' },
  verbose: { bg: '#1a3d1a', text: '#a5d6a7' },
  debug: { bg: '#2c2c2c', text: '#bdbdbd' },
  silly: { bg: '#1e1e1e', text: '#9e9e9e' },
};

const LogViewer: React.FC<LogViewerProps> = React.memo(({
  logs,
  autoScroll = true,
  maxHeight = 500,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const levelColors = isDark ? darkLevelColors : lightLevelColors;

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const formatMeta = (meta?: Record<string, any>) => {
    if (!meta || Object.keys(meta).length === 0) return null;

    // Filter out common metadata we don't want to display
    const filtered = { ...meta };
    delete filtered.service;
    delete filtered.timestamp;
    delete filtered.level;
    delete filtered.message;

    if (Object.keys(filtered).length === 0) return null;

    try {
      return JSON.stringify(filtered);
    } catch {
      return null;
    }
  };

  if (logs.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: maxHeight,
          bgcolor: isDark ? 'background.paper' : '#fafafa',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">No logs to display</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        maxHeight,
        overflow: 'auto',
        bgcolor: isDark ? 'background.paper' : '#fafafa',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        p: 1,
        fontFamily: 'monospace',
        fontSize: '12px',
        lineHeight: 1.5,
      }}
    >
      {logs.map((log, index) => {
        const colors = levelColors[log.level] || levelColors.info;
        const meta = formatMeta(log.meta);

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              py: 0.25,
              borderBottom: 1,
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Typography
              component="span"
              sx={{
                color: 'text.secondary',
                fontSize: '11px',
                minWidth: '70px',
                flexShrink: 0,
              }}
            >
              {formatTimestamp(log.timestamp)}
            </Typography>

            <Chip
              label={log.level.toUpperCase()}
              size="small"
              sx={{
                height: '18px',
                fontSize: '10px',
                fontWeight: 600,
                bgcolor: colors.bg,
                color: colors.text,
                minWidth: '55px',
                flexShrink: 0,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  color: 'text.primary',
                  fontSize: '12px',
                  wordBreak: 'break-word',
                }}
              >
                {log.message}
              </Typography>

              {meta && (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '11px',
                    ml: 1,
                    wordBreak: 'break-all',
                  }}
                >
                  {meta}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});

LogViewer.displayName = 'LogViewer';

export default LogViewer;
